import api from './api';

/* School name, contact details and logo (Settings > Print & receipt), used on every printout. */
let cached = null;
let loading = null;
const listeners = new Set();

export const branding = () => cached;

export const loadBranding = (force = false) => {
  if (cached && !force) return Promise.resolve(cached);
  if (loading && !force) return loading;
  loading = api.get('/auth/branding/')
    .then((r) => {
      cached = r.data;
      listeners.forEach((fn) => fn(cached));
      return cached;
    })
    .catch(() => cached)
    .finally(() => { loading = null; });
  return loading;
};

export const setBranding = (data) => {
  cached = data;
  listeners.forEach((fn) => fn(cached));
};

export const onBranding = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export const contactLine = (b = cached) => {
  if (!b) return '';
  return [b.address, b.phone && `Tel ${b.phone}`, b.email, b.website, b.cr_number && `CR ${b.cr_number}`].filter(Boolean).join('  |  ');
};

export const PRINT_HEADER_CSS = `
  .doc-head{display:flex;align-items:center;gap:14px;border-bottom:2px solid #333;padding-bottom:8px;margin-bottom:12px}
  .doc-head img{max-height:64px;max-width:160px;object-fit:contain}
  .doc-head .org{font-size:17px;font-weight:bold}
  .doc-head .contact{font-size:10px;color:#555;margin-top:2px}
  .doc-head .title{margin-left:auto;font-size:16px;font-weight:bold;text-align:right}
`;

/** Header block for print windows built in the browser. */
export const printHeaderHtml = (title = '', sub = '') => {
  const b = cached || {};
  return `<div class="doc-head">${b.logo ? `<img src="${b.logo}" alt="">` : ''}
    <div><div class="org">${esc(b.org_name || 'Al Samriya Riding School')}</div><div class="contact">${esc(contactLine(b))}</div></div>
    <div class="title">${esc(title)}${sub ? `<div class="contact">${esc(sub)}</div>` : ''}</div></div>`;
};

/** Opens a print page made by the server (it already carries the logo and header). */
export const openServerPrint = (path, onError) => {
  const w = window.open('', '_blank');
  if (!w) {
    if (onError) onError('Allow popups to print');
    return;
  }
  fetch(`${api.defaults.baseURL}${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
    .then((r) => (r.ok ? r.text() : Promise.reject(r)))
    .then((html) => {
      w.document.write(html);
      w.document.close();
      w.focus();
    })
    .catch(() => {
      w.close();
      if (onError) onError('Print failed');
    });
};

export const orgName = () => cached?.org_name || 'Al Samriya Riding School';

/** Draws the logo (on a white box) inside a box of maxW x maxH in the PDF's unit; returns the width used (0 without a logo). */
export const pdfLogo = (doc, x, y, maxW, maxH, alignRight = false) => {
  const logo = cached?.logo;
  if (!logo) return 0;
  try {
    const p = doc.getImageProperties(logo);
    let h = maxH;
    let w = h * (p.width / p.height);
    if (w > maxW) { w = maxW; h = w * (p.height / p.width); }
    const left = alignRight ? x - w : x;
    doc.setFillColor(255, 255, 255);
    doc.rect(left - 2, y - 2, w + 4, h + 4, 'F');
    doc.addImage(logo, p.fileType || 'PNG', left, y, w, h);
    return w;
  } catch {
    return 0;
  }
};

/**
 * Draws the logo, school name and contact line at the top of a jsPDF page and returns the y where content starts.
 * Works with any unit; sizes are given in millimetres and converted.
 */
export const pdfHeader = (doc, title = '', sub = '') => {
  const b = cached || {};
  const k = doc.internal.scaleFactor / (72 / 25.4);
  const mm = (v) => v / k;
  const left = mm(10);
  const top = mm(8);
  const pageW = doc.internal.pageSize.getWidth();
  let x = left;
  const logoH = mm(14);
  if (b.logo) {
    try {
      const p = doc.getImageProperties(b.logo);
      const w = Math.min(logoH * (p.width / p.height), mm(40));
      doc.addImage(b.logo, p.fileType || 'PNG', left, top, w, w * (p.height / p.width));
      x = left + w + mm(4);
    } catch {
      x = left;
    }
  }
  doc.setTextColor(0);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(13);
  doc.text(b.org_name || 'Al Samriya Riding School', x, top + mm(5));
  doc.setFont(undefined, 'normal');
  doc.setFontSize(8);
  const contact = contactLine(b);
  if (contact) doc.text(contact, x, top + mm(9.5));
  if (title) {
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text(title, pageW - left, top + mm(5), { align: 'right' });
    doc.setFont(undefined, 'normal');
  }
  if (sub) {
    doc.setFontSize(8);
    doc.text(sub, pageW - left, top + mm(9.5), { align: 'right' });
  }
  const y = top + logoH + mm(2);
  doc.setDrawColor(60);
  doc.setLineWidth(mm(0.5));
  doc.line(left, y, pageW - left, y);
  doc.setFontSize(10);
  return y + mm(4);
};
